import { useState } from 'react';
import { generatePath, useLoaderData, useLocation, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { createDraftCatalog, updateDraftCatalog } from '../../common/fetch';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import {
  companyMsg,
  deviceListSavedAsDraftMsg,
  newDeviceListMsg,
  nextMsg,
  saveMsg,
  savingTheDataFailedMsg,
  selectCompanyHeaderMsg,
  t,
} from '../../common/i18n';
import { getPrimaryMdmId, isMultiBiz } from '../../common/utils/accountUtils';
import { CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE } from '../../common/utils/billingAccountUtils';
import { ContractPeriod } from '../../common/utils/catalogUtils.js';
import { userHasEmptyOrSMEPriceGroup } from '../../common/utils/employeeUtils.js';
import { isEppSolutionActive } from '../../common/utils/stateUtils.js';
import { getMdmIdFromUrlParams } from '../../common/utils/urlUtils';
import { Catalog, EppCategory } from '../../generated/api/models.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { getActiveAccountMasterId } from '../../selfservice/common/localStorageUtils';
import { billingAccountDefaultValues } from '../BillingAccount/BillingAccountFieldset.js';
import { deliveryMethodDefaultValues } from '../BillingAccount/DeliveryMethodFieldset.js';
import { CompanySelector } from '../CompanySelector/CompanySelector';
import { getUserAccounts } from '../Header/dynamic/headerFunctions';
import { CatalogConfigurationForm, mapFormValuesToBeProcessed } from './CatalogConfigurationForm.js';
import { createBillingAccountForCatalog } from './utils';
import type { CatalogDamageInsurance, FormValues } from './CatalogConfigurationForm.js';
import type { CatalogProduct } from '../../common/utils/catalogUtils.js';
import type {
  BillChannel,
  BillingAccount,
  BillingAccountHeader,
  CompanyInfoResponse,
  VirtualCatalog,
} from '../../generated/api/models.js';
import type { AuthenticatedUser } from '../../public/site/authState';

import './CatalogConfiguration.scss';

export interface CatalogConfigurationProps {
  companyInfo: CompanyInfoResponse;
  user: AuthenticatedUser | undefined;
}

export interface CatalogConfigurationEditProps extends CatalogConfigurationProps {
  mdmId: string;
  virtualCatalog: VirtualCatalog;
}

export interface CatalogParams {
  catalog: Partial<Catalog>;
  products?: Record<string, Array<CatalogProduct>>;
  virtualCatalog?: VirtualCatalog;
  billingAccount: BillingAccount;
  billingAccountHeader?: BillingAccountHeader;
}

const getConfigValues = (props: CatalogConfigurationProps) => {
  const companyInfo = props.companyInfo;
  const isEppActive = isEppSolutionActive(props.companyInfo);
  const isSmeOrEmptyPriceGroup = userHasEmptyOrSMEPriceGroup(props.user?.segmentPricingGroup);

  const isAllowedForLEPricingGroup = isEppActive ? companyInfo?.eppSolution?.allowDevicesWithRecurringCharges : false;
  const isAllowedForSmeOrEmptyPricingGroup = !isEppActive || companyInfo?.eppSolution?.allowDevicesWithRecurringCharges;
  const isRecurringChargeAllowed = isSmeOrEmptyPriceGroup
    ? isAllowedForSmeOrEmptyPricingGroup
    : isAllowedForLEPricingGroup;
  return { isEppActive, isRecurringChargeAllowed };
};

export const CatalogConfiguration = (props: CatalogConfigurationProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = props;
  const mdmIdInitialValue = isMultiBiz(user)
    ? getMdmIdFromUrlParams() || getPrimaryMdmId(user)
    : getActiveAccountMasterId();
  const [mdmId, setMdmId] = useState(mdmIdInitialValue);
  const { search } = useLocation();
  const { isEppActive, isRecurringChargeAllowed } = getConfigValues(props);
  const { billChannels } = useLoaderData() as { billChannels: BillChannel[] };
  const onSubmit = async (values: FormValues) => {
    navigate(paths.COMPANY_INFO_CATALOG_LIST_PRODUCTS + search, {
      state: { ...mapFormValuesToBeProcessed(values), mdmId },
    });
  };
  const catalogValues = {
    name: location.state?.catalog.name || '',
    productType:
      location.state?.catalog?.productType ||
      (isEppActive ? Catalog.ProductTypeEnum.EPP_RECURRING : Catalog.ProductTypeEnum.RECURRING),
    contractPeriod: location.state?.catalog?.contractPeriod
      ? location.state.catalog.contractPeriod.toString()
      : `${ContractPeriod.MONTHS_24}`,
    damageInsurance: location.state?.catalog?.damageInsurance || [],
    corporateShare: location.state?.catalog?.corporateShare
      ? (location.state.catalog.corporateShare / 100).toFixed(2)
      : '',
    corporateMessage: location.state?.catalog?.corporateMessage,
    enrollmentProgramConsent: location.state?.catalog?.enrollmentProgramConsent || false,
    approverMustSelectBillingAccount: location.state?.catalog?.approverMustSelectBillingAccount || false,
    enrollmentProgramAlias: location.state?.catalog?.enrollmentProgramAlias,
    billingAccountId: location.state?.catalog?.billingAccountId,
    selectedBillingAccountIds: location.state?.catalog?.selectedBillingAccountIds || [],
  };

  return (
    <div>
      {isMultiBiz(user) && (
        <>
          <h3 className={`${dsClass.H3_BOOK} ${dsClass.MARGIN_TOP_7}`}>{t.BR9A(selectCompanyHeaderMsg)}</h3>
          <p>{t.A0OH('Select the company for which you want to create a catalog.')}</p>
          <CompanySelector
            userAccounts={getUserAccounts(user)}
            onInputChange={(_, option) => {
              navigate('?mdmId=' + option.accountMasterId, { replace: true });
              setMdmId(option.accountMasterId!);
            }}
            initialMdmId={mdmIdInitialValue || undefined}
            labelText={t.KJTS(companyMsg)}
          />
        </>
      )}

      <h3>{t.MPFC(newDeviceListMsg)}</h3>
      <p>
        {t.B0GP(
          `You can use the device list to set up your company's device portfolio. There can be more than one device list, and you can select one or more devices for a list. Also note, only products of one catalog list can be ordered together, so create list accordingly. You can also list the price limit that the company pays for each device per month. When the OmaLasku service is activated, the employee can also choose a device above the price limit, in which case they will pay the difference themselves.`
        )}
      </p>

      <CatalogConfigurationForm
        isEppActive={isEppActive}
        isRecurringChargeAllowed={Boolean(isRecurringChargeAllowed)}
        isOnetimeChargeAllowed={Boolean(!isEppActive || props.companyInfo?.eppSolution?.allowDevicesWithOneTimeCharges)}
        onSubmit={onSubmit}
        cancelHref={paths.COMPANY_INFO_CATALOGS}
        submitButtonMsg={t.F0MY(nextMsg)}
        defaultValues={
          location.state?.defaultValues || {
            billingAccount: {
              ...billingAccountDefaultValues(props.companyInfo),
              ...deliveryMethodDefaultValues(billChannels),
            },
            catalog: catalogValues,
            isCorporateMessageAdded: Boolean(location.state?.catalog?.corporateMessage),
          }
        }
        mdmId={mdmId || undefined}
      />
    </div>
  );
};

export const CatalogConfigurationEdit = ({ virtualCatalog, ...rest }: CatalogConfigurationEditProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mdmId } = useSearchParams<{ mdmId: string }>();
  const { billChannels } = useLoaderData() as { billChannels: BillChannel[] };
  const draftOrPublishedVc = virtualCatalog.draft || virtualCatalog.published;
  if (!draftOrPublishedVc) {
    return null;
  }
  const { isEppActive, isRecurringChargeAllowed } = getConfigValues(rest);

  const onSubmit = async (values: FormValues) => {
    const readyFormValues = mapFormValuesToBeProcessed(values);
    try {
      const newBillingAccountResponse =
        readyFormValues.catalog?.billingAccountId === CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE
          ? await createBillingAccountForCatalog(readyFormValues, mdmId)
          : undefined;
      const billingAccountId = newBillingAccountResponse?.billingAccountId || readyFormValues.catalog?.billingAccountId;

      const catalog: Catalog = {
        ...draftOrPublishedVc,
        ...readyFormValues.catalog,
        billingAccountId,
        // Ensure products is never undefined
        products: draftOrPublishedVc.products || [],
      };

      // If we already have a draft, we update it, otherwise we create a new draft
      if (virtualCatalog.draft) {
        await updateDraftCatalog(virtualCatalog.virtualCatalogCode, catalog, mdmId);
      } else {
        await createDraftCatalog(virtualCatalog.virtualCatalogCode, catalog, mdmId);
      }

      addNotification(t.P7JV(deviceListSavedAsDraftMsg));
      navigate(
        `${generatePath(paths.COMPANY_INFO_CATALOG, { catalogCode: virtualCatalog.virtualCatalogCode })}?mdmId=${location.state.mdmId || mdmId}`,
        newBillingAccountResponse
          ? { state: { newBillingAccountDisplayId: newBillingAccountResponse.billingAccountDisplayId } }
          : undefined
      );
    } catch (e) {
      addNotification(t.RQA1(savingTheDataFailedMsg), 'error');
    }
  };

  return (
    <CatalogConfigurationForm
      isEppActive={isEppActive}
      isRecurringChargeAllowed={Boolean(isRecurringChargeAllowed)}
      isOnetimeChargeAllowed={Boolean(!isEppActive || rest.companyInfo?.eppSolution?.allowDevicesWithOneTimeCharges)}
      submitButtonMsg={t.NIWE(saveMsg)}
      onSubmit={onSubmit}
      cancelHref={`${generatePath(paths.COMPANY_INFO_CATALOG, { catalogCode: virtualCatalog.virtualCatalogCode })}?mdmId=${location.state.mdmId || mdmId}`}
      defaultValues={{
        billingAccount: {
          ...billingAccountDefaultValues(rest.companyInfo),
          ...deliveryMethodDefaultValues(billChannels),
        },
        catalog: {
          name: draftOrPublishedVc.name,
          productType: draftOrPublishedVc.productType,
          contractPeriod: draftOrPublishedVc.contractPeriod?.toString(),
          // Damage insurance is coming from checkbox, which doesn't have a placeholder EPP category option
          damageInsurance: (draftOrPublishedVc.damageInsurance || []).filter(
            item => item !== EppCategory.PLACEHOLDER
          ) as CatalogDamageInsurance,
          corporateShare: draftOrPublishedVc.corporateShare ? (draftOrPublishedVc.corporateShare / 100).toFixed(2) : '',
          corporateMessage: draftOrPublishedVc.corporateMessage,
          enrollmentProgramConsent: draftOrPublishedVc.enrollmentProgramConsent,
          enrollmentProgramAlias: draftOrPublishedVc.enrollmentProgramAlias,
          billingAccountId: draftOrPublishedVc.billingAccountId,
          approverMustSelectBillingAccount: draftOrPublishedVc.approverMustSelectBillingAccount,
          selectedBillingAccountIds: draftOrPublishedVc.selectedBillingAccountIds,
        },
        isCorporateMessageAdded: Boolean(draftOrPublishedVc.corporateMessage),
      }}
      editing={true}
      mdmId={mdmId || undefined}
    />
  );
};
